import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CriterionAverageItem,
  DashboardBlockId,
  HiddenCriterionRef,
  NameValueItem,
  QualityConsultantReportResponse,
  QualityKpis,
  QualitySummaryResponse,
} from '@/entities/dashboards';
import type { ConversationSelectionSource } from '@/entities/conversations';
import { qualityZoneByRatio, type QualityZoneRanges } from '@/shared/lib';
import type { IndicatorSelectHandler } from '../model/useIndicatorTransitions';
import { KpiRow } from './KpiRow';
import { BarChartCard } from './parts/BarChartCard';
import { CriteriaAveragesCard } from './CriteriaAveragesCard';
import { SummaryCard } from './SummaryCard';
import { TopCallsCard } from './TopCallsCard';
import { AlertsCard } from './AlertsCard';
import { QualityZonesCard } from './QualityZonesCard';
import { ConsultantStabilityCard } from './ConsultantStabilityCard';
import { ConsultantTrendsCard } from './ConsultantTrendsCard';
import { InsightsCard } from './InsightsCard';
import { ConsultantReportCard } from './ConsultantReportCard';
import type { BarChartItem } from './parts/BarChart';

/**
 * Единый максимум шкалы карточки «Рейтинг консультантов»: есть, только когда у каждой строки все
 * оценки сделаны по одной шкале и шкала эта у всех строк одна и та же. Тогда карточка показывает
 * баллы вместе с ним; `null` — сравнимой шкалы у карточки нет, и все строки идут долями.
 *
 * Размерность выбирается один раз на карточку, а не по строке: строки читаются как список для
 * сравнения, и балл одной строки рядом с долей соседней сравнить нельзя.
 */
const consultantRatingScale = (items: NameValueItem[]): number | null => {
  let scale: number | null = null;
  for (const item of items) {
    if (!item.sameScale || item.maxScore === null || item.maxScore <= 0) return null;
    if (scale !== null && scale !== item.maxScore) return null;
    scale = item.maxScore;
  }
  return scale;
};

/**
 * Строка рейтинга консультантов: полоса заполнена средней долей его баллов от их собственных
 * максимумов, поэтому оценки по заключениям с разными шкалами не складываются в один балл по
 * чужой шкале. Консультант без доли заполнения полосы не получает — она минимальной ширины.
 *
 * Что стоит рядом с полосой, решает вся карточка: со шкалой карточки (`scale`) все строки
 * показывают балл с этим максимумом, без неё — все долю в процентах. Доля, которую определить
 * нельзя, величины не даёт — такая строка остаётся без значения.
 *
 * По той же доле полоса и красится: зона — свойство доли от максимума, а не абсолютного балла.
 * Без доли максимум определить нельзя — окраски по зоне у полосы нет.
 */
const toConsultantRatingItem = (
  item: NameValueItem,
  zoneRanges: QualityZoneRanges,
  scale: number | null,
): BarChartItem => {
  const share = item.avgShare;
  const sharePct = share === null ? null : share * 100;
  return {
    label: item.name,
    value: scale === null ? sharePct : item.value,
    suffix: scale === null ? '%' : undefined,
    maxScore: scale,
    share,
    zone: share === null ? undefined : qualityZoneByRatio(share, zoneRanges),
    conversationIds: item.conversationIds,
  };
};

/** Пропсы сводных виджетов дашборда (KPI, графики, аналитика, мини-отчёт). */
type Props = {
  /** Идентификаторы скрытых блоков дашборда (настройка отображения). */
  hiddenBlocks: DashboardBlockId[];
  summaryData: QualitySummaryResponse | undefined;
  kpis: QualityKpis | null;
  /** Средние по критериям без скрытых пользователем — для графика «Критерии» и сводки. */
  visibleCriteriaAverages: CriterionAverageItem[];
  /** Максимум для нормировки графика распределения. */
  distributionMax: number;
  /** Максимум для нормировки графика динамики. */
  trendMax: number;
  /** Обработчики перехода к выборке диалогов для каждого источника показателя. */
  selectHandlers: Record<ConversationSelectionSource, IndicatorSelectHandler>;
  /** Открытие одного диалога; `openInNewTab` — клик с shift. */
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
  consultantNames: string[];
  /** ФИО консультанта мини-отчёта; пустая строка — не выбран. */
  reportConsultant: string;
  reportData: QualityConsultantReportResponse | undefined;
  onChangeReportConsultant: (name: string) => void;
  /** Критерии, скрытые пользователем, — каждый в разрезе своего заключения (алерты и мини-отчёт). */
  hiddenCriteria: HiddenCriterionRef[];
  /** Пользовательские пороги зон качества (рейтинг консультантов, критерии, мини-отчёт). */
  zoneRanges: QualityZoneRanges;
};

/**
 * Сводные карточки дашборда качества (Presenter): строка KPI, графики (рейтинг, критерии,
 * низкие оценки, распределение, динамика, сводка), аналитика качества и консультантов, а
 * также мини-отчёт по консультанту. Блок из `hiddenBlocks` не рендерится.
 */
export const DashboardSummarySection: FC<Props> = ({
  hiddenBlocks,
  summaryData,
  kpis,
  visibleCriteriaAverages,
  distributionMax,
  trendMax,
  selectHandlers,
  onOpenConversation,
  consultantNames,
  reportConsultant,
  reportData,
  onChangeReportConsultant,
  hiddenCriteria,
  zoneRanges,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const isBlockVisible = (id: DashboardBlockId) => !hiddenBlocks.includes(id);

  const consultantRatings = summaryData?.consultantRatings ?? [];
  const ratingScale = consultantRatingScale(consultantRatings);

  return (
    <>
      {isBlockVisible('kpiRow') && kpis && kpis.count > 0 && (
        <KpiRow kpis={kpis} visibleCriteriaAverages={visibleCriteriaAverages} />
      )}

      {/* Ряд 1: Рейтинг | Критерии | Низкие оценки */}
      {isBlockVisible('ratingsCharts') && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <BarChartCard
            title={t('charts.consultantRating')}
            items={consultantRatings.map((item) => toConsultantRatingItem(item, zoneRanges, ratingScale))}
            onSelect={selectHandlers.consultantRating}
            capHeight
          />
          <CriteriaAveragesCard
            title={t('charts.criteriaAverages')}
            criteria={visibleCriteriaAverages}
            zoneRanges={zoneRanges}
            onSelect={selectHandlers.criteriaAverage}
          />
          <BarChartCard
            title={t('charts.lowScores')}
            items={(summaryData?.lowScores ?? []).map((b) => ({
              label: b.name,
              value: b.pct,
              suffix: '%',
              conversationIds: b.conversationIds,
            }))}
            maxValue={100}
            negative
            onSelect={selectHandlers.lowScore}
            capHeight
          />
        </div>
      )}

      {/* Ряд 2: Распределение | Динамика | Сводка */}
      {isBlockVisible('distributionRow') && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <BarChartCard
            title={t('charts.distribution')}
            items={(summaryData?.distribution ?? []).map((b) => ({
              label: b.label,
              value: b.count,
              conversationIds: b.conversationIds,
            }))}
            maxValue={distributionMax}
            onSelect={selectHandlers.distribution}
          />
          <BarChartCard
            title={t('charts.trend')}
            items={(summaryData?.trend ?? []).map((b) => ({
              label: b.period,
              value: b.avg,
              conversationIds: b.conversationIds,
            }))}
            maxValue={trendMax}
            onSelect={selectHandlers.trend}
          />
          <SummaryCard
            kpis={kpis}
            criteriaAverages={visibleCriteriaAverages}
            reportSummaries={summaryData?.reportSummaries ?? []}
          />
        </div>
      )}

      {/* Ряд 3: Топ худших | Топ лучших | Красные зоны */}
      {isBlockVisible('topCallsRow') && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <TopCallsCard
            title={t('topCalls.worst')}
            calls={summaryData?.topWorst ?? []}
            negative
            onOpenCall={onOpenConversation}
          />
          <TopCallsCard title={t('topCalls.best')} calls={summaryData?.topBest ?? []} onOpenCall={onOpenConversation} />
          <AlertsCard
            alerts={summaryData?.alerts ?? []}
            hiddenCriteria={hiddenCriteria}
            onSelectAlert={selectHandlers.alert}
          />
        </div>
      )}

      {/* Ряд 4: Аналитика качества и консультантов (перенос виджетов прототипа) */}
      {isBlockVisible('analyticsRow') && (
        <>
          <QualityZonesCard zones={summaryData?.qualityZones ?? []} onSelectZone={selectHandlers.qualityZone} />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <ConsultantStabilityCard items={summaryData?.consultantStability ?? []} />
            <ConsultantTrendsCard items={summaryData?.consultantTrends ?? []} />
            <InsightsCard insights={summaryData?.insights ?? []} onSelectInsight={selectHandlers.autoInsight} />
          </div>
        </>
      )}

      {isBlockVisible('consultantReport') && (
        <ConsultantReportCard
          consultantName={reportConsultant}
          consultantNames={consultantNames}
          report={reportData}
          onChangeConsultant={onChangeReportConsultant}
          hiddenCriteria={hiddenCriteria}
          zoneRanges={zoneRanges}
        />
      )}
    </>
  );
};
